<template>
  <v-container fluid grid-list-md>
    <v-layout row wrap align-center>
      <v-flex xs12 sm8>
        <h2 class="headline">Network Management</h2>
        <div class="grey--text body-1">
          The NMS that manages each FNO's sites (one per FNO). TelecoGlue reaches it through a connector on this server
          (http://127.0.0.1:&lt;port&gt;). The connector's key is not entered here: it goes in Platform/server/.env as shown on each FNO,
          followed by an API restart.
        </div>
      </v-flex>
      <v-flex xs12 sm4 class="text-sm-right">
        <v-btn flat color="primary" :loading="loading" @click="load">
          <v-icon left>refresh</v-icon>Refresh
        </v-btn>
      </v-flex>
    </v-layout>

    <v-layout row wrap>
      <v-flex xs12 md6 v-for="fno in fnos" :key="fno.id">
        <v-card>
          <v-card-title>
            <div>
              <div class="title">{{ fno.name }}</div>
              <div class="grey--text caption">Organisation {{ fno.id }}</div>
            </div>
            <v-spacer></v-spacer>
            <v-chip small :color="fno.nmsType ? 'primary' : 'grey'" text-color="white">{{ typeLabel(fno.nmsType) }}</v-chip>
          </v-card-title>

          <v-card-text>
            <div v-if="fno.zhone" class="body-1">
              Zhone ZMS through ZhoneMiddleman ({{ fno.networkAPI || "no address" }}). Managed outside this page.
            </div>
            <div v-else>
              <v-layout row wrap>
                <v-flex xs12 sm5>
                  <v-select :items="types" v-model="fno.edit.nmsType" label="NMS" @change="suggestAddress(fno)"></v-select>
                </v-flex>
                <v-flex xs12 sm7>
                  <v-text-field v-model="fno.edit.networkAPI" label="Connector address" :disabled="!fno.edit.nmsType"
                    :placeholder="placeholder(fno.edit.nmsType)" hint="http://127.0.0.1:<port>" persistent-hint></v-text-field>
                </v-flex>
              </v-layout>
              <div class="caption mt-2">
                <v-icon small :color="fno.keyConfigured ? 'green' : 'orange'">{{ fno.keyConfigured ? "check_circle" : "warning" }}</v-icon>
                Connector key {{ fno.keyName }}: {{ fno.keyConfigured ? "set in .env" : "not set in .env" }}
              </div>
              <div v-if="fno.result" class="caption mt-2" :class="fno.result.ok ? 'green--text' : 'red--text'">
                <div>{{ fno.result.summary }}</div>
                <div v-for="olt in fno.result.olts" :key="olt.oltId" class="grey--text text--darken-1">
                  OLT {{ olt.oltId }}: {{ olt.reachable ? "reachable" : "not reachable" }}
                </div>
              </div>
            </div>

            <div class="subheading mt-3">Sites</div>
            <div v-if="!fno.networks.length" class="grey--text caption">No networks.</div>
            <div v-for="n in fno.networks" :key="n.id" class="body-1">
              {{ n.name }}
              <span class="grey--text caption">{{ n.addresses || 0 }} addresses, {{ n.olts || 0 }} OLT{{ n.olts == 1 ? "" : "s" }} recorded</span>
            </div>
          </v-card-text>

          <v-card-actions v-if="!fno.zhone">
            <v-spacer></v-spacer>
            <v-btn flat :disabled="!changed(fno)" :loading="fno.saving" @click="save(fno)">Save</v-btn>
            <v-btn flat color="primary" :disabled="!fno.networkAPI || changed(fno)" :loading="fno.testing" @click="test(fno)">
              Test connection
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-flex>
    </v-layout>

    <v-snackbar v-model="snack" :timeout="6000" bottom>{{ snackText }}</v-snackbar>
  </v-container>
</template>

<script>
import Api from "@/services/Api";

// Ports from NmsConnectors/SPEC.md §1
const PORTS = { bdcom: 7520, ubiquiti: 7530 };

export default {
  name: "NmsSettings",
  data() {
    return {
      fnos: [],
      loading: false,
      types: [
        { text: "None", value: null },
        { text: "BDCOM", value: "bdcom" },
        { text: "Ubiquiti UISP", value: "ubiquiti" }
      ],
      snack: false,
      snackText: ""
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      Api()
        .get("/admin/nms")
        .then(res => {
          this.fnos = res.data.map(f => Object.assign({}, f, {
            edit: { nmsType: f.nmsType, networkAPI: f.networkAPI || "" },
            saving: false,
            testing: false,
            result: null
          }));
        })
        .catch(e => this.notify(e, "Could not load the FNOs."))
        .finally(() => (this.loading = false));
    },
    typeLabel(type) {
      return { zhone: "Zhone ZMS", bdcom: "BDCOM", ubiquiti: "Ubiquiti UISP" }[type] || "No NMS";
    },
    placeholder(type) {
      return PORTS[type] ? "http://127.0.0.1:" + PORTS[type] : "";
    },
    suggestAddress(fno) {
      if (!fno.edit.nmsType) fno.edit.networkAPI = "";
      else if (!fno.edit.networkAPI) fno.edit.networkAPI = this.placeholder(fno.edit.nmsType);
    },
    changed(fno) {
      return fno.edit.nmsType !== fno.nmsType || (fno.edit.networkAPI || "") !== (fno.networkAPI || "");
    },
    save(fno) {
      fno.saving = true;
      Api()
        .put("/admin/nms/" + fno.id, { nmsType: fno.edit.nmsType, networkAPI: fno.edit.networkAPI })
        .then(res => {
          fno.nmsType = res.data.nmsType;
          fno.networkAPI = res.data.networkAPI;
          fno.edit.networkAPI = res.data.networkAPI || "";
          fno.result = null;
          this.notify(null, fno.name + " saved.");
        })
        .catch(e => this.notify(e, "Could not save."))
        .finally(() => (fno.saving = false));
    },
    test(fno) {
      fno.testing = true;
      fno.result = null;
      Api()
        .get("/admin/nms/" + fno.id + "/health")
        .then(res => {
          const body = res.data.body || {};
          if (body.ok) {
            const d = body.data || {};
            fno.result = {
              ok: true,
              summary: "Connected: " + (d.vendor || "?") + " connector " + (d.version || "") + ", writes " + (d.writes || "?"),
              olts: d.olts || []
            };
          } else {
            const err = body.error || {};
            fno.result = {
              ok: false,
              summary: (err.message || "The connector answered with HTTP " + res.data.httpStatus) + (err.code ? " (" + err.code + ")" : ""),
              olts: []
            };
          }
        })
        .catch(e => (fno.result = { ok: false, summary: this.message(e, "Could not test the connector."), olts: [] }))
        .finally(() => (fno.testing = false));
    },
    message(e, fallback) {
      return (e && e.response && e.response.data && e.response.data.message) || fallback;
    },
    notify(e, text) {
      this.snackText = e ? this.message(e, text) : text;
      this.snack = true;
    }
  }
};
</script>
